/**
 * IntakeReviewPanel.tsx — "here is what we found inside your ZIP".
 *
 * The screen this lives on used to show a bundle as ONE row called
 * `something.zip`, discard everything the classifier had already worked out
 * about its ~30 members, and compress the whole result into one grey sentence
 * pointing at a decision that had to be made on a different route. This panel is
 * that discarded work, put on screen.
 *
 * Four product rules it exists to honour, none of them implementation taste:
 *
 * 1. **The app proposes, the Admin vetoes.** Every KB-eligible document starts
 *    CHECKED. An empty list to fill in loses the classifier's work — the user
 *    ticks the two filenames they recognise and drops the sixteen they don't.
 * 2. **The four required documents carry no checkbox** (Omar, 2026-08-14). They
 *    are mandatory for the analysis, so there is nothing to choose. Three of
 *    them are not KB material at all; the Relazione is, and rides along with the
 *    batch automatically — see `useKbDecision`.
 * 3. **Decide on what a file CONTAINS.** Filenames look like `23086-D-ED-G-DDP_00`.
 *    The Italian kind, the page count and the document's own opening lines are
 *    what make the judgement possible; the filename alone is not a basis for
 *    spending money.
 * 4. **It never blocks the analysis.** Amended by `P-31` (2026-08-19) and worth
 *    stating precisely, because the wording used to be "the analysis has already
 *    auto-started by the time this renders" and that is now false: this panel is
 *    what the user reads BEFORE pressing Avvia. What survives is the substance —
 *    nothing in the Knowledge Base column can prevent an analysis. A caller who
 *    may not decide the KB still starts the run; a KB submission that fails
 *    leaves the analysis running; and an empty KB list is not a refusal. See
 *    `analysisState` below, which is how this panel knows which of the two
 *    worlds it is in instead of assuming.
 *
 * No € figures anywhere: Revo is billed a flat fee, so a euro number is noise to
 * the customer and exposes our unit economics. Pages are the honest proxy.
 */
import { useEffect, useMemo, useState, useRef } from "react";

import { I } from "../../components/icons";
import { type DocumentDownload } from "../../lib/useDocumentDownload";
import {
  DOC_ROLE_LABEL,
  REQUIRED_ANALYZE_ROLES,
  useDocumentKinds,
  type PriceListLibrary,
  type ProjectPriceList,
  type TenderDocRole,
  type TenderDocument,
} from "../../lib/tenderApi";
import { type DocumentCorrection } from "../../lib/useDocumentCorrection";
import { pagesLabel, plural, recapLines, type KbDecision } from "../../lib/useKbDecision";
import { KbReceipt, receiptColor } from "./KbReceipt";

const ROLE_OPTIONS: TenderDocRole[] = [
  "cme",
  "full_price",
  "np",
  "info",
  "regional",
  "drawing",
  "other",
];

type GroupId = "required" | "kb" | "analysis" | "attachment";

interface Group {
  id: GroupId;
  title: string;
  note: string;
  docs: TenderDocument[];
  /** Collapsed by default — groups with nothing to decide. */
  collapsible: boolean;
}

/**
 * Split the documents into the four semantic groups, first match wins.
 *
 * `analysisUse` is NOT "used", it is "something reads inside this file" — the
 * Quadro Economico, the safety costs, the manodopera and the listino all carry
 * it, and the reconciliation check compares against them. So the third group is
 * never labelled "unused"; that would be false and would invite someone to
 * delete a document the totals check depends on.
 */
export function groupDocuments(
  documents: TenderDocument[],
  kb: KbDecision,
  /** Whether the reader may actually act on a checkbox. REQUIRED, not optional
      with a permissive default: the group note instructs the reader to untick
      things, and a default of `true` is exactly how the one call site that
      matters keeps the wrong copy. */
  canDecide: boolean,
): Group[] {
  const required: TenderDocument[] = [];
  const kbProposed: TenderDocument[] = [];
  const analysis: TenderDocument[] = [];
  const attachment: TenderDocument[] = [];
  const pendingIds = new Set(kb.optional.map((d) => d.id));
  // Documents already DECIDED belong to the Knowledge Base story too, not to the
  // attachment bucket. Routing them by `analysisUse` alone put a document showing
  // an "In Knowledge Base" pill under a heading whose note reads "nessuno dei due
  // sistemi li legge" — the screen contradicting itself about the same row.
  const decidedIds = new Set(kb.decided.map((d) => d.id));

  for (const d of documents) {
    if (REQUIRED_ANALYZE_ROLES.includes(d.role)) required.push(d);
    else if (pendingIds.has(d.id) || decidedIds.has(d.id)) kbProposed.push(d);
    else if (d.analysisUse) analysis.push(d);
    else attachment.push(d);
  }

  const byPlace = (a: TenderDocument, b: TenderDocument): number =>
    (a.folderPath ?? "").localeCompare(b.folderPath ?? "", "it") ||
    a.filename.localeCompare(b.filename, "it");

  required.sort(
    (a, b) => REQUIRED_ANALYZE_ROLES.indexOf(a.role) - REQUIRED_ANALYZE_ROLES.indexOf(b.role),
  );
  kbProposed.sort(byPlace);
  analysis.sort(byPlace);
  attachment.sort(byPlace);

  return [
    {
      id: "required",
      title: `Richiesti dall'analisi (${new Set(required.map((d) => d.role)).size} di 4)`,
      note: "Questi documenti sono obbligatori per l'analisi e non si possono escludere: per questo non hanno una casella. Se uno è stato riconosciuto male, correggi il tipo.",
      docs: required,
      collapsible: false,
    },
    {
      id: "kb",
      // The count must describe the ROWS beneath it. Counting only the pending
      // ones put "(2)" over four rendered rows — two of which have no checkbox —
      // under a note telling the user to untick what they don't want.
      title:
        pendingIds.size > 0
          ? kbProposed.length > pendingIds.size
            ? `Knowledge Base (${kbProposed.length}) — ${pendingIds.size} da confermare`
            : /* When a locked-in document exists, these are two DIFFERENT
                  quantities saying the same words. `kbProposedNote` counts every
                  `pending` document — the locked-in Relazione included — and
                  this counts only the rows carrying a checkbox, because the
                  locked-in one is filed under `required` by product decision
                  (Omar, 2026-08-14). So "3 documenti proposti per la Knowledge
                  Base" sat above "Proposti per la Knowledge Base (2)", in all
                  three walks, both roles, every phase, with nothing to reconcile
                  them. Changing the COUNT would put a number over a list that
                  does not hold that many rows; changing the WORDS says what this
                  group actually is — the ones that are yours to choose.

                  Conditional on purpose: where there is no locked-in document
                  the two counts agree and "Proposti" is exactly right, so the
                  wording only changes in the state that was ambiguous. */
              kb.lockedIn.length > 0
              ? `Da scegliere per la Knowledge Base (${pendingIds.size})`
              : `Proposti per la Knowledge Base (${pendingIds.size})`
          : `Knowledge Base (${kbProposed.length} ${kbProposed.length === 1 ? "già deciso" : "già decisi"})`,
      note:
        pendingIds.size > 0
          ? canDecide
            ? "Preselezionati da noi. Togli la spunta a quelli che non vuoi sintetizzare. La spunta riguarda solo la Knowledge Base: nessuno di questi file esce dall'analisi."
            : "Preselezionati da noi. La conferma spetta a un amministratore. La scelta riguarda solo la Knowledge Base: nessuno di questi file esce dall'analisi."
          : "Su questi documenti la scelta è già stata fatta. Puoi aggiungerne altri più avanti dalla pagina dei documenti.",
      docs: kbProposed,
      collapsible: false,
    },
    {
      id: "analysis",
      title: `Letti dall'analisi, non destinati alla Knowledge Base (${analysis.length})`,
      note: "Servono ai controlli — quadro economico, listini, incidenza della manodopera. Non c'è niente da scegliere.",
      docs: analysis,
      collapsible: true,
    },
    {
      id: "attachment",
      title: `Allegati conservati, non elaborati (${attachment.length})`,
      note: "Fuori dall'analisi, ma sempre con te. Nel pacchetto finale li scarichi tutti, già convertiti da .p7m in PDF.",
      docs: attachment,
      collapsible: true,
    },
  ];
}

/**
 * `KB-19` — exported so «Documenti del progetto» shows the SAME words for the
 * same state. A second copy of this map is how two screens end up disagreeing
 * about whether a document is in the Knowledge Base, and the copy that drifts
 * is the one nobody is looking at.
 */
export function KbStatePill({ state }: { state: string }): JSX.Element | null {
  if (state === "forwarded") return <span className="pill pill-ok">In Knowledge Base</span>;
  if (state === "queued" || state === "forwarding")
    return <span className="pill pill-info">Invio in corso</span>;
  if (state === "failed") return <span className="pill pill-err">Invio non riuscito</span>;
  if (state === "skipped") return <span className="pill">Escluso</span>;
  return null;
}

function DocumentRow({
  doc,
  variant,
  checked,
  onToggle,
  canDecide,
  correction,
  decisionBusy,
  kindOptions,
  download,
}: {
  doc: TenderDocument;
  variant: "required" | "checkbox" | "plain";
  checked: boolean;
  onToggle: () => void;
  canDecide: boolean;
  correction: DocumentCorrection;
  /** A decision is in flight — the checkbox is closed for its duration. */
  decisionBusy: boolean;
  kindOptions: { value: string; labelIt: string }[];
  download: DocumentDownload;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const dim = variant === "checkbox" && !checked;
  const lines = recapLines(doc.titleBlock, 2);
  const pages = pagesLabel(doc.pageCount);
  const busyKind = correction.isBusy(doc.id, "docKind");
  const busyRole = correction.isBusy(doc.id, "role");

  // Always offer the stored kind even when the taxonomy no longer lists it —
  // otherwise the select silently displays a DIFFERENT document's type.
  const kinds = kindOptions.some((k) => k.value === doc.docKind)
    ? kindOptions
    : [{ value: doc.docKind, labelIt: doc.labelIt || doc.docKind }, ...kindOptions];

  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        padding: "9px 12px",
        borderRadius: 8,
        alignItems: "flex-start",
        /* `P-39` `L2-02` — the outline and the lift the prototype gives every
           row. Without them the `--c-bg-elev` fill below is invisible (it is
           the same white as the panel), so "chosen" was communicated only by
           the ABSENCE of dimming on its neighbours — a signal that disappears
           entirely when every row is chosen, which is the default state. The
           shadow is the prototype's own two-part one, and it lands only on a
           row that has actually been decided, so the lift means something. */
        background: variant === "checkbox" && checked ? "var(--c-bg-elev)" : "transparent",
        border: "1px solid var(--c-line)",
        boxShadow:
          variant === "checkbox" && checked
            ? "0 1px 0 rgba(20, 22, 28, 0.04), 0 2px 6px rgba(20, 22, 28, 0.05)"
            : "none",
        opacity: dim ? 0.55 : 1,
        /* 2px on BOTH branches, and never `transparent`: this declaration comes
           after `border` and overrides its left edge, so a transparent value
           would leave every non-required card open on one side — the outline
           added just above would have been three-sided. Same width either way
           keeps the content aligned between the two variants. */
        borderLeft: variant === "required" ? "2px solid var(--c-brand)" : "2px solid var(--c-line)",
      }}
      aria-busy={busyKind || busyRole ? true : undefined}
    >
      {/* One 20px gutter for the whole list, so the eye reads a single column of
          controls in which the required entries are fixed. A DISABLED CHECKED
          checkbox is deliberately not used: it reads as "someone chose this and
          you cannot unchoose it" and a screen reader announces it on the same
          axis as the real decisions, which is the confusion this panel removes. */}
      <div style={{ width: 20, flexShrink: 0, paddingTop: 2 }}>
        {variant === "checkbox" ? (
          <input
            type="checkbox"
            checked={checked}
            /* The VISIBLE half of the in-flight rule; `useKbDecision.toggle`
               carries the enforcing half. Both, because an attribute is a
               statement to the browser and a change event reaches the handler
               regardless of it — twice in this build already. */
            disabled={!canDecide || decisionBusy}
            onChange={onToggle}
            aria-label={doc.filename}
          />
        ) : variant === "required" ? (
          <>
            <I.CheckC size={15} style={{ color: "var(--c-ok)" }} aria-hidden />
            <span className="sr-only">Sempre incluso nell&apos;analisi</span>
          </>
        ) : null}
      </div>

      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>
            {doc.labelIt || doc.docKind}
            {pages && <span style={{ color: "var(--c-ink-3)", fontWeight: 400 }}> · {pages}</span>}
          </span>
          {variant === "required" && <span className="pill pill-brand">Richiesto</span>}
          {doc.needsReview && (
            <span className="pill pill-warn">
              <I.Alert size={10} /> Da confermare
            </span>
          )}
          <KbStatePill state={doc.kbForwardState} />
        </div>

        {/* WHY, not how sure. Ratified by Omar 2026-08-28: the pill used to read
            «Da confermare (48%)», and 48% was `Signal.page_geometry`'s ordering
            weight — a number that looked like a probability of being wrong and
            was nothing of the kind. The sentence names what to look at when the
            document is opened, which is the decision this row asks for.

            Rendered as its own line rather than inside the pill: the reasons run
            to «Nome del file e formato della pagina non coerenti», which inside a
            pill would either wrap it into a block or be truncated.

            The sentence comes from the server (`review_reason_it`), so a flagged
            document from before migration 0017 has none and this simply does not
            render — the same absence the row showed before, minus the number. */}
        {doc.needsReview && doc.reviewReasonIt && (
          <div style={{ fontSize: 11.5, color: "var(--c-warn)", marginTop: 3 }}>
            {doc.reviewReasonIt}
            {/* Both codes, joined, never one chosen — Omar, 2026-08-28: *"retain
                both references… this makes the discrepancy visible without
                guessing which value is correct."* Shown beside the reason rather
                than on the filename line above, which is `nowrap` + ellipsis and
                would silently truncate exactly the half that is news. */}
            {doc.declaredDocCode && doc.docCode && (
              <span className="t-mono" style={{ color: "var(--c-ink-3)" }}>
                {" "}
                {doc.declaredDocCode}_or_{doc.docCode}
              </span>
            )}
          </div>
        )}

        <div
          style={{
            fontSize: 11.5,
            color: "var(--c-ink-3)",
            marginTop: 2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={doc.filename}
        >
          {doc.filename}
          {doc.folderPath && (
            <span>
              {" · "}
              <I.Folder size={10} style={{ verticalAlign: -1 }} /> {doc.folderPath}
            </span>
          )}
        </div>

        {lines.length > 0 && (
          <div
            style={{
              fontSize: 11,
              color: "var(--c-ink-3)",
              marginTop: 5,
              lineHeight: 1.45,
              borderLeft: "2px solid var(--c-line)",
              paddingLeft: 8,
            }}
            aria-label="Prime righe del documento"
          >
            {lines.map((l, i) => (
              <div
                key={i}
                style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {l}
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6 }}>
          <button
            className="btn btn-ghost btn-sm"
            style={{ height: 24, fontSize: 11.5 }}
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {open ? <I.ChevronU size={11} /> : <I.ChevronD size={11} />} Correggi
          </button>
          {/* `INT-9` — the customer cannot decide what a document IS from a filename
              and two lines of recap, and for a technical drawing there is no recap to
              read: `INT-8` stops extracting its text on purpose. This is the escape
              hatch that makes "ask a human" an answerable question. */}
          <button
            className="btn btn-ghost btn-sm"
            style={{ height: 24, fontSize: 11.5 }}
            onClick={() => download.open(doc)}
            disabled={download.isBusy(doc.id)}
            // `aria-label`, NOT `title`: the row's filename element carries the only
            // `title` in a row and a QA sweep counts rows by exactly that attribute
            // (`p37r3.corpus`, "a row is a node carrying the document's own filename
            // tooltip"). A second `title` per row doubled every count. It is also the
            // better accessible name — thirty buttons all called "Scarica" are not
            // distinguishable to a screen reader.
            aria-label={`Scarica ${doc.filename}`}
          >
            <I.Download size={11} /> {download.isBusy(doc.id) ? "Attendere…" : "Scarica"}
          </button>
        </div>
        {download.errorFor(doc.id) && (
          <div role="alert" style={{ marginTop: 4, fontSize: 11.5, color: "var(--c-danger)" }}>
            Impossibile scaricare il documento. Riprova.
          </div>
        )}

        {open && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginTop: 8,
              padding: 10,
              background: "var(--c-bg-sunken)",
              borderRadius: 8,
            }}
          >
            <div>
              <label
                style={{ fontSize: 11, fontWeight: 500, color: "var(--c-ink-2)" }}
                htmlFor={`kind-${doc.id}`}
              >
                Tipo di documento
              </label>
              <select
                id={`kind-${doc.id}`}
                className="input"
                value={doc.docKind}
                disabled={busyKind}
                aria-label={`Tipo di ${doc.filename}`}
                onChange={(e) => void correction.changeKind(doc, e.target.value)}
                style={{ width: "100%", marginTop: 3, fontSize: 12, padding: "4px 8px" }}
              >
                {kinds.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.labelIt}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                style={{ fontSize: 11, fontWeight: 500, color: "var(--c-ink-2)" }}
                htmlFor={`role-${doc.id}`}
              >
                Ruolo nell&apos;analisi
              </label>
              <select
                id={`role-${doc.id}`}
                className="input"
                value={doc.role}
                disabled={busyRole}
                aria-label={`Ruolo di ${doc.filename}`}
                onChange={(e) => void correction.changeRole(doc, e.target.value as TenderDocRole)}
                style={{ width: "100%", marginTop: 3, fontSize: 12, padding: "4px 8px" }}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {DOC_ROLE_LABEL[r]}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ gridColumn: "1 / -1", fontSize: 11, color: "var(--c-ink-3)" }}>
              Correggendo il tipo aggiorniamo anche il ruolo nell&apos;analisi e la destinazione
              Knowledge Base.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Which group this instance puts the reader's attention on. `P-31`. */
/**
 * Which step this panel is showing.
 *
 * `"all"` — the undivided panel — was deleted on 2026-08-20 (Omar). `P-37`'s tab
 * strip gave every state an explicit step, leaving `UploadView` with one call
 * site that can only produce these two; the third branch of the header, its
 * sub-copy and `defaultCollapsed(null)` had become code the product could not
 * reach, and no test asserted its heading. A dead branch on a screen that spends
 * money is a place for a future reader to add behaviour nobody can see.
 *
 * `P-43` (Omar, 2026-08-22) — a view now REMOVES groups, not just emphasis.
 * Passo 1 renders the required documents alone, exactly as the prototype's
 * mode1 list does; Passo 2 renders the Knowledge Base set plus the collapsed
 * attachments. The `analysis` bucket renders on NEITHER step — the prototype
 * has no such group anywhere, and Omar ordered the module removed. Those
 * documents stay in the project and on the Documents page; nothing about the
 * partition changes, only what the wizard shows. The old worry — "the remedy
 * would point at rows not on screen" — is met by the missing-role row itself,
 * whose assignment select lists EVERY document in the project regardless of
 * which groups render.
 */
export type ReviewView = "required" | "kb";

/**
 * The direct-upload exit on a missing-required-document row (`P-43`).
 *
 * Owned by `UploadView`, which holds the project id, the upload mutation and
 * its invalidations; this panel only renders the controls. `activeRole` is the
 * role an upload is currently in flight for (one at a time — the control locks
 * while it works), `pct` is the REAL bytes-sent percentage from the XHR
 * transport (`P-41`) or null before the first event, and `error` is the last
 * failure, shown on the row that caused it.
 */
export interface MissingRoleUpload {
  start: (role: TenderDocRole, file: File) => void;
  activeRole: TenderDocRole | null;
  activeName: string | null;
  pct: number | null;
  /** The row the last failure belongs to — errors render where they happened. */
  errorRole: TenderDocRole | null;
  error: string | null;
}

/**
 * The «Listini prezzi di riferimento» card on Passo 1 (`P-44`).
 *
 * Two kinds of row, two mechanisms, worded differently on screen: a LIBRARY
 * edition (auto-associated from region detection, or removed via the
 * association endpoint) and an UPLOADED listino (an ordinary project document
 * with role='regional', removed like any document). What this card lists and
 * what the analysis stages into its regional directory are the same set —
 * that shared consumer is what keeps the card honest.
 */
export interface PriceListsPanel {
  /**
   * Whether `editions` is an ANSWER or merely the absence of one.
   *
   * An errored query has `data === undefined` exactly like a pending one, so
   * `data ?? []` rendered «Nessun listino associato … senza confronto prezzi» —
   * a confident claim that no price comparison will run — over a project whose
   * association the runner WILL stage. Same rule as `kbProgress` 113 lines up:
   * gate on success, never on emptiness.
   */
  ready: boolean;
  editions: ProjectPriceList[];
  /** Remove a library association. The edition itself is untouched. */
  remove: (editionId: string) => void;
  /** Archive an uploaded listino document. */
  removeDoc: (doc: TenderDocument) => void;
  removeBusy: boolean;
  /** Upload a listino — lands as a project document with role='regional'. */
  upload: (file: File) => void;
  uploading: { name: string; pct: number | null } | null;
  error: string | null;
  /** True once the run has started — the controls stand down (prototype `locked`). */
  locked: boolean;
  /**
   * The library the picker draws from (`P-47b`), or NULL while it is not an
   * answer — pending, errored, or not asked for.
   *
   * Nullable rather than a `libraryReady` flag beside a list: a guarantee
   * belongs inside the value the consumer already reads, or the consumer
   * forgets to honour it. `editions` above needed a separate `ready` and an
   * errored query rendered «Nessun listino associato» over a project that had
   * one; a `null` cannot be destructured by accident.
   */
  library: PriceListLibrary | null;
  /** Associate a library edition. Resurrects a tombstone server-side. */
  add: (editionId: string) => void;
  addBusy: boolean;
  /** `P-55` — download the exact workbook this project prices against.
   *
   * Its own downloader rather than the document one: the two mint URLs from
   * different endpoints, and sharing a busy latch would make downloading a
   * price book appear to disable a document row that happened to share an id.
   */
  download: DocumentDownload;
}

/**
 * «Piemonte, Valle d'Aosta ed Emilia-Romagna» (`P-47b`).
 *
 * The list is built from what the API says the library HOLDS, never from a
 * literal: the mockup's «sono disponibili i prezzari di tutte le regioni» was
 * false over three regions, and a hard-coded replacement goes false again the
 * day an edition is seeded. `ed` before a vowel is not decoration — this copy
 * is read by Italian professionals and «e Emilia-Romagna» reads as a typo.
 */
export function joinRegions(regions: string[]): string {
  if (regions.length === 0) return "";
  if (regions.length === 1) return regions[0];
  const head = regions.slice(0, -1).join(", ");
  const last = regions[regions.length - 1];
  return `${head} ${/^[aeiouAEIOU]/.test(last) ? "ed" : "e"} ${last}`;
}

/** The workbook this edition's title should download, or null (`P-55`).
 *
 * Replaces `safeHref`/`sourceHost`, which rendered the operator-typed
 * `source_url`. Omar ruled on 28 Aug that the link must serve OUR stored file
 * instead — and that removes a whole class rather than guarding it: there is
 * now no path by which an operator-supplied string reaches an `href`, so the
 * `javascript:` parse guard has nothing left to defend. `source_url` is still
 * carried on the model as provenance; it is simply not a link.
 *
 * Null in THREE cases, and only one of them is explained on screen: the edition
 * owns no workbook · the server is older than this release and sent no `id` ·
 * the book is licensed and may not be redistributed (`P-58`). In all three the
 * title renders as plain text rather than as a control that cannot work; the
 * licensed case additionally prints its reason, because a customer owed the
 * sentence *"this is the file that priced you"* deserves to know why they are
 * not being handed it.
 *
 * `files[0]`, and the ordering is the SERVER's: an edition with several
 * sections (Piemonte publishes 32) shows its first here and the rest through
 * the «N file» line. Picking one is deliberate — the title is one control, so
 * it needs one target.
 */
function downloadableFile(
  e: ProjectPriceList,
): { id: string; filename: string; section: string | null } | null {
  // `P-58`: `redistributable` is checked HERE as well as trusted through the
  // absent id, and the redundancy is deliberate rather than defensive noise.
  // The id-is-null rule lives on the server; this one lives beside the control
  // it removes, so a future server that starts sending ids again — or a fixture
  // that sets one — cannot quietly re-open a download of a licensed book. The
  // endpoint refuses independently; that is the third.
  if (!e.redistributable) return null;
  const f = e.files[0];
  return f && f.id ? { id: f.id, filename: f.filename, section: f.section } : null;
}

/** The detector's own words, short enough to sit on one line. */
function truncateEvidence(matchedOn: string, max = 90): string {
  const first = matchedOn.split(";")[0].trim();
  return first.length > max ? `${first.slice(0, max - 1)}…` : first;
}

/** Where the run is, which decides whether this panel may commit anything. */
export type AnalysisState = "not_started" | "running" | "finished";

/**
 * What the Knowledge Base header may claim about a started run.
 *
 * It used to be the flat sentence *"La selezione è stata confermata all'avvio
 * dell'analisi"*, printed whenever a job existed. A job existing is not a batch
 * being accepted, and for the role every ordinary live user holds the two are
 * never the same event: `startRun` submits the batch only for a `kb:approve`
 * holder, so a `member` pressing Avvia starts the analysis, sends nothing, and
 * was then told their selection had been confirmed — beside a bar reading
 * *"Solo un amministratore può confermare questa scelta"* in the same card.
 *
 * `sent` is server-derived on purpose: `kb.decided` counts documents the server
 * has actually moved out of `pending`, rather than `kb.done`/`kb.error`, which
 * are `useState` inside the hook and describe THIS session's last request rather
 * than the state of the batch.
 *
 * That conclusion is right; an earlier version of this comment reached it by a
 * false route. It argued that `kb.done` "is wiped by the reload the background-job
 * doctrine requires to be survivable" — and **that reload cannot land on this
 * screen**: `IngestPage` holds its view in `useState` and the only entry is
 * «nuovo progetto», so a reload lands on the Dashboard. Right conclusion, false
 * premise, which is the class this project keeps recording. The real argument is
 * simpler: a claim about what the SERVER holds has to be read from the server.
 *
 * Exported for the same reason `startBlockedBy` is: a sentence chosen inside a
 * JSX ternary has no failing state a test can reach.
 */
export function kbConfirmationLine(s: {
  /** Documents the SERVER has moved out of `pending` — `kb.decided.length`. */
  sent: number;
  /** Still awaiting a decision — `optional + lockedIn`. */
  awaiting: number;
  canDecide: boolean;
}): string {
  /* Scoped to the SELECTION, not the page — the unscoped version was false.
     The per-row «Correggi» button carries no `canDecide` gate and neither
     correction <select> is disabled by it, so a reader without `kb:approve` can
     change document types on the page this sentence called read-only. Worse,
     the three guards written against exactly this class in exactly this panel
     all match capital-S "Sola lettura" while this sentence is lowercase, and
     all render with `canDecide = true`, so none of them could ever have seen
     it. Same defect as the header's unscoped «Sola lettura», one sentence over,
     and it survived the pass that fixed that one. */
  const readOnlyTail = "Per te la selezione per la Knowledge Base è in sola lettura.";
  if (s.sent === 0 && s.awaiting === 0) {
    /* Nothing was sent because there was never anything to send. Tested BEFORE
       the `sent === 0` branch: that branch printed "scegli quali sintetizzare e
       premi «Conferma la selezione»" over a group rendering no rows at all, a
       button reading «Conferma la selezione (0)» and disabled, and a summary
       reading "Nessun documento selezionato". Three claims on one screen, and
       the only actionable one impossible. */
    return "Questo progetto non ha documenti da sintetizzare nella Knowledge Base.";
  }
  if (s.sent === 0) {
    // Nothing has been sent. Say that, and do not name WHO may act — the commit
    // bar below states that on the control it concerns, and saying it here too
    // put the same sentence on screen twice in two consecutive rounds.
    // NOT "Nessun documento …": the commit bar's own summary opens with
    // "Nessun documento selezionato" when the batch is empty, and a query
    // written against one of them started matching both. Two sentences opening
    // on the same three words, about two different subjects, in one card — the
    // matcher reporting it was evidence about the page, not noise in the test.
    return s.canDecide
      ? "Questi documenti non sono ancora stati inviati alla Knowledge Base: scegli quali sintetizzare e premi «Conferma la selezione»."
      : `Questi documenti non sono ancora stati inviati alla Knowledge Base. ${readOnlyTail}`;
  }
  if (s.awaiting === 0) {
    // Everything is decided, so there is nothing left to change — and the old
    // copy's "si può ancora cambiare" was false here for the same reason the
    // headline was: it described an intention rather than a state.
    return "La selezione è stata confermata: non resta niente da decidere per la Knowledge Base.";
  }
  const rest = `${s.awaiting} ${plural(s.awaiting, "documento aspetta ancora una decisione", "documenti aspettano ancora una decisione")}.`;
  return s.canDecide
    ? `Una parte della selezione è già stata confermata; ${rest} La Knowledge Base è separata e l'analisi non ne è toccata.`
    : `Una parte della selezione è già stata confermata; ${rest} ${readOnlyTail}`;
}

/** The group a view focuses. Every view focuses one; there is no undivided panel. */
function focusOf(view: ReviewView): GroupId {
  return view === "kb" ? "kb" : "required";
}

/** Everything except the focused group starts closed — unless it is holding a
 *  document that needs confirming.
 *
 *  Ratified by Omar 2026-08-28: *"Open the section automatically only when it
 *  contains a document requiring confirmation."* Before this, a flagged drawing
 *  sat in the `attachment` group, which starts closed on Passo 2 — so the reason
 *  the row now carries (`INT-12`) was one click away from the person being asked
 *  to act on it, while the header toggle already counted it. The narrow rule is
 *  the point: a group with nothing to confirm stays closed, so the screen is no
 *  noisier than before on the ordinary bundle.
 *
 *  `asking` is a comma-joined key rather than the group array, and deliberately.
 *  The effect below re-seeds on it, and `groups` is a `useMemo` over `kb` — a
 *  hook result whose identity is not guaranteed stable across renders. Depending
 *  on the array would re-run the effect every render and `setCollapsed` a fresh
 *  Set each time, which is a render loop, not a feature. A string compares by
 *  value. */
function defaultCollapsed(focus: GroupId, asking = ""): Set<GroupId> {
  const all: GroupId[] = ["required", "kb", "analysis", "attachment"];
  const open = new Set<string>([focus, ...asking.split(",").filter(Boolean)]);
  return new Set(all.filter((id) => !open.has(id)));
}

export function IntakeReviewPanel({
  documents,
  kb,
  correction,
  download,
  onlyReview,
  setOnlyReview,
  canDecide,
  view,
  analysisState,
  missingUpload,
  priceLists,
}: {
  documents: TenderDocument[];
  kb: KbDecision;
  /** Owned by `UploadView` for the same reason `correction` is — see below. */
  download: DocumentDownload;
  /**
   * Owned by `UploadView`, not by this panel. `P-37` made the panel a
   * conditional child (`activeTab !== "3"`), and the correction hook lived
   * inside it — so switching to Avanzamento inside a PATCH round trip unmounted
   * the only surface that renders `correction.error`, and a REFUSED type
   * correction reported nowhere at all. `setError` on an unmounted component is
   * a silent no-op in React 18, the request is not aborted, and on remount
   * `busyEdits` is a fresh empty Set: the select re-enables showing the OLD
   * value with no alarm anywhere. The header copy on this very panel invites
   * correcting a type mid-run, so this is the primary path, not a corner.
   *
   * Lifting it also keeps `isBusy` alive across the tab change, which is why the
   * per-row control stops lying about being idle.
   */
  correction: DocumentCorrection;
  /**
   * The «Da confermare» filter, owned by `UploadView`.
   *
   * It lives up there because it is a PRECONDITION OF A COMMIT, and the commit
   * that matters is up there too — see the note beside `commitBlockedByFilter`.
   * Lifting it also survives the tab-3 round trip, which used to destroy it
   * silently along with the reader's place in the list.
   */
  onlyReview: boolean;
  setOnlyReview: (v: boolean) => void;
  /** Whether this user may confirm the KB choice (`kb:approve`). */
  canDecide: boolean;
  /**
   * Which step of the confirm-then-start wizard this is, if any.
   *
   * Each view renders only its OWN step's groups — see `visibleGroups`. Passo 1
   * is the four required documents as a headerless list, and nothing else.
   *
   * (This used to say the opposite — that a view "moves the emphasis and never
   * removes a group" — which was the pre-`P-43` contract. The remedy for a
   * misfiled document is no longer "find it in a collapsed group": it is the
   * missing-role row's assignment select, which lists every document in the
   * project regardless of which groups render.)
   */
  view: ReviewView;
  /**
   * What the analysis is doing — the panel's own commit bar exists only once
   * the run has started.
   *
   * Passed rather than inferred. The bar's sub-line used to assert "L'analisi è
   * già in corso e non aspetta questa scelta" unconditionally, which was already
   * false on the finished screen and would have become a much worse lie under
   * `P-31`, where this panel renders BEFORE anything has started and the single
   * Avvia button commits both.
   */
  analysisState: AnalysisState;
  /** The second exit on a missing-required row — see `MissingRoleUpload`. */
  missingUpload: MissingRoleUpload;
  /** The «Listini prezzi di riferimento» card's data and actions — see `PriceListsPanel`. */
  priceLists: PriceListsPanel;
}): JSX.Element {
  const kindOptions = useDocumentKinds().data ?? [];
  const focus = focusOf(view);
  const [collapsed, setCollapsed] = useState<Set<GroupId>>(() => defaultCollapsed(focusOf(view)));
  // Re-seeded when the step changes, so Avanti/Indietro moves the open group.
  // Keyed on `focus` rather than on `view` — historically because an "all" →
  // "all" render (every
  // render once the run has started) never stamps on a disclosure the user
  // just opened.
  /* D-11 — a tab switch used to close a group the reader had just opened.
     The effect below re-seeds `collapsed` whenever `focus` changes; its own
     comment says it was keyed on `focus` rather than `view` SO THAT a
     constant-`view` render could never stamp on a disclosure. `P-37` retired the
     constant "all" view, so every switch between Passo 1 and Passo 2 now changes
     `focus` — and the protection no longer covered the browsing it was written
     for. Measured: `aria-expanded` false → true → **false** across one round
     trip. Once the reader has touched a group, their state wins. */
  const touched = useRef(false);
  /** Only the wizard's own button may commit while the run has not started. */
  const showCommit = analysisState !== "not_started";

  const groups = useMemo(
    () => groupDocuments(documents, kb, canDecide),
    [documents, kb, canDecide],
  );
  /**
   * `P-43` — which groups this STEP renders, not which it emphasises.
   *
   * Passo 1 is the four required documents and nothing else (the prototype's
   * mode1 list); Passo 2 is the Knowledge Base set plus the collapsed
   * attachments (its else-branch). The `analysis` bucket is in neither list —
   * Omar ordered the «Letti dall'analisi, non destinati» module removed, and
   * the prototype never had it. The partition itself is unchanged, so nothing
   * downstream (KB counts, veto reconciliation, badges) moves.
   */
  const visibleGroups = useMemo(
    () =>
      view === "required"
        ? groups.filter((g) => g.id === "required")
        : groups.filter((g) => g.id === "kb" || g.id === "attachment"),
    [groups, view],
  );
  /** The groups holding at least one document waiting to be confirmed, as a
   *  value-comparable key. See `defaultCollapsed` for why this is a string. */
  const askingGroups = useMemo(
    () =>
      groups
        .filter((g) => g.docs.some((d) => d.needsReview))
        .map((g) => g.id)
        .sort()
        .join(","),
    [groups],
  );
  useEffect(() => {
    if (touched.current) return;
    setCollapsed(defaultCollapsed(focus, askingGroups));
  }, [focus, askingGroups]);

  const pendingIds = useMemo(() => new Set(kb.optional.map((d) => d.id)), [kb.optional]);
  /**
   * Scoped to the docs THIS step renders, not to the whole project (`P-43`).
   * Unscoped, the button promised "Da confermare (3)" on a step where zero of
   * the three flagged rows could ever appear — the filter would have narrowed
   * the list to nothing while claiming there was work here. A flagged document
   * in the `analysis` bucket is reachable from the Documents page, where every
   * document renders.
   */
  const needsReviewCount = visibleGroups.flatMap((g) => g.docs).filter((d) => d.needsReview).length;
  /**
   * The «Da confermare» filter and the commit that must not fire under it.
   *
   * Measured on the real TORINO bundle: its 5 flagged documents and its 18
   * KB-eligible documents are DISJOINT — every flag is a `drawing`, whose
   * `kbForwardState` is `not_applicable`. One click on the filter removes the
   * "Proposti per la Knowledge Base (17)" group from the screen entirely, while
   * a control offers to send all 18. That breaks the rule this file states
   * twice: a group carrying a commit is on screen on the step whose button
   * commits it.
   *
   * TWO attempts are recorded here, because the first was wrong in an
   * instructive way. Disabling this panel's own commit button did nothing for
   * the WIZARD, where `showCommit` is false and the panel has no bar at all —
   * the control that spends the money there is `UploadView`'s sticky «Avvia
   * analisi e Knowledge Base», which cannot see a flag held in this component.
   * A guard on the cheaper of two buttons is not a guard. The second attempt
   * exempted the focused group from the filter, which closed the class and cost
   * the filter its usefulness on the one group people actually filter; eight
   * tests said so, and they were right.
   *
   * So the STATE moved instead. `onlyReview` is owned by `UploadView`, beside
   * the commit that has to respect it: this panel renders the toggle and reads
   * the flag, and the wizard's Avvia leaves the Knowledge Base out of the press
   * — saying so in its own label — rather than sending a list nobody has seen.
   * Nothing is blocked; the analysis never waits on the Knowledge Base, which
   * is rule 4 at the top of this file.
   */
  const commitBlockedByFilter = onlyReview;
  /** The two facts the header may state about the batch, both server-derived. */
  const kbSent = kb.decided.length;
  const kbAwaitingHere = kb.optional.length + kb.lockedIn.length;

  const missingRoles = REQUIRED_ANALYZE_ROLES.filter((r) => !documents.some((d) => d.role === r));
  /**
   * R2 §2.1 + §2.3 — why this REPORTS rather than FILTERS.
   *
   * Round 1 found the select offering every document, so "Nuovi Prezzi is
   * missing" could be repaired with the sole CME and the row simply changed
   * which required role it complained about. The obvious fix — drop those
   * options — was worse, and QA measured it: in a bundle where the real Nuovi
   * Prezzi came back as `info` and the real Relazione as `other`, the CORRECT
   * answer was the one removed (sole holder of `info`), the only option left was
   * the wrong document, the user took it, the alarm went out, and a paid run
   * proceeded on the wrong file. Filtering cost more than the defect did. And on
   * an ordinary "they forgot to upload it" bundle the filter emptied the list
   * entirely, so the control vanished under a sentence promising it.
   *
   * So every document is always listed. One that is the SOLE holder of another
   * required role is `disabled` and says which role it would empty. The reader
   * sees the whole truth: what is here, what would cost something, and — when
   * every option is disabled — that the answer is to upload the file.
   */
  function soleRequiredHolder(d: TenderDocument): TenderDocRole | null {
    if (!REQUIRED_ANALYZE_ROLES.includes(d.role)) return null;
    return documents.filter((x) => x.role === d.role).length > 1 ? null : d.role;
  }
  /**
   * R2 §2.2 — the authority, not a fifth guard.
   *
   * D-9 added a local `assigning` scalar to stop a second pick inside the round
   * trip. It could not see the per-row «Correggi» control's in-flight state, so
   * with a role change already open B9 stayed live, the pick reached
   * `correct()`, the held `docId:role` key returned early, and the assignment
   * vanished in silence — the exact defect, on the path the new guard did not
   * cover. `useDocumentCorrection` already knows; this asks it. Four guards have
   * now been written around this one behaviour, so the fifth is not another
   * scalar.
   */
  const assignBusy = documents.some(
    /* BOTH fields. `changeKind` re-derives `role` server-side, so a kind
       correction in flight on the same document is writing the very field this
       control writes — QA measured two concurrent PATCHes on one document, one
       rewriting the other's field, because this only watched `role`. */
    (d) => correction.isBusy(d.id, "role") || correction.isBusy(d.id, "docKind"),
  );

  /** The ids a bulk control may touch: rendered here, and still the user's to decide. */
  function visibleKbIds(rows: TenderDocument[]): string[] {
    return rows.filter((d) => pendingIds.has(d.id)).map((d) => d.id);
  }

  /**
   * A heading must describe the rows underneath it.
   *
   * The group titles are computed over the FULL list, which is right until the
   * "Da confermare" filter narrows what is rendered — then "Knowledge Base (4) —
   * 2 da confermare" sat, unchanged, over a single row. The bulk buttons were
   * scoped and renamed for exactly this reason; the heading had not followed.
   */
  function headingFor(g: Group, rows: TenderDocument[]): string {
    // Only a group that was actually narrowed gets the narrowed heading. The
    // focused group is exempt, so its own title still describes its rows, and
    // "— N da confermare" over an unfiltered list would be the heading lying
    // about what is underneath it — the very defect this function prevents.
    if (!onlyReview) return g.title;
    return `${g.title.replace(/\s*\([^)]*\).*$/, "")} — ${rows.length} da confermare`;
  }

  function toggleGroup(id: GroupId): void {
    setCollapsed((prev) => {
      const next = new Set(prev);
      touched.current = true;
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="card" style={{ overflow: "hidden" }}>
      <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--c-line)" }}>
        {/* `P-37`/B1 — no "Passo 1 di 2" here any more. The tab strip above says
            which step this is, and says it as something you can act on; a
            heading repeating it was the position stated twice and offered
            nowhere. The document COUNT stays: it is the only place the panel
            says how much it read. */}
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          {view === "required"
            ? `I documenti del computo (${documents.length} letti in tutto)`
            : "La conoscenza dell'azienda, che resta."}
        </div>
        {/* `P-37`/B8 — read-only is stated, not implied.

            Before this, a started run showed the same instructions as a run
            waiting to be confirmed ("controlla… poi continua al passo 2"),
            with the commit bar quietly changed underneath. Saying it outright
            costs one line and removes the question.

            "Sola lettura" is about THIS STEP's confirmation, not about the
            rows: correcting a document's type stays available for the life of
            the project, which is how a misclassification found after a failed
            run gets fixed before the retry. The copy below says so. */}
        <div style={{ fontSize: 12, color: "var(--c-ink-3)", marginTop: 3 }}>
          {/* R4-01 — branched on `analysisState`, which has THREE values, not on
              `showCommit`, which has two. Reading the two-valued one printed
              "l'analisi è già avviata e gira su questi documenti" on the Passo 1
              of a FINISHED run — directly under a notice reading "L'analisi è
              terminata" and, on a failed run, under a red "Analisi non riuscita"
              alert. The commit bar 300 lines below already made this
              distinction; the header did not. */}
          {showCommit && view === "required" ? (
            /* "in questo passo" — the subject was missing, and its absence was
               read as a claim about the whole page. One disclosure click on this
               same step opens the Knowledge Base group, whose checkboxes and
               both bulk controls are LIVE for a `kb:approve` holder, under a
               heading telling them to untick things. Three reachable paths put a
               reader there, one of them by design: a member starts the run, so
               nothing is sent, and an admin opening the project later finds
               every document still pending.

               The round-5 co-presence detector walks this exact cell and reports
               it clean, because these two sentences are catalogued on the
               `run-tense` axis and the controls on `kb-mutability`, and axes are
               never compared. Scoping the sentence is the fix; the catalogue
               entry is the alarm, and it is the tester's to place. */
            <>
              <strong>Sola lettura in questo passo:</strong>{" "}
              {analysisState === "running"
                ? "l'analisi è già avviata e gira su questi documenti."
                : "l'analisi è finita e ha girato su questi documenti."}{" "}
              Puoi comunque correggere un tipo sbagliato — vale per il progetto e per la prossima
              analisi, non per quella già partita.
              {canDecide && kbAwaitingHere > 0 && (
                <> La scelta per la Knowledge Base, invece, è ancora aperta nel Passo 2.</>
              )}
            </>
          ) : showCommit && view === "kb" ? (
            /* R4-03 — NOT "sola lettura". It was, and it was false: the
               checkboxes below are live, both bulk controls are there, and
               «Conferma la selezione» sends a real batch — QA pressed it from
               under this sentence and measured the payload. Read-only in this
               panel looks like the `canDecide=false` case, where every checkbox
               really is disabled and the button is absent. The selection was
               confirmed at the start and can still be changed; say that. */
            /* Says WHAT, not WHO. The commit bar below already states "Solo un
               amministratore può confermare questa scelta" for a non-admin, on
               the control it concerns — and saying it here too put the same
               sentence on screen twice. Caught by TC-018 reporting "Found
               multiple elements", which is evidence about the page rather than
               about the query. */
            /* One string, chosen by an exported predicate. It was a ternary on
               `canDecide` under a flat assertion that a batch had been sent at
               the start — see `kbConfirmationLine` for why that assertion was
               false for the role every ordinary live user holds. */
            <>{kbConfirmationLine({ sent: kbSent, awaiting: kbAwaitingHere, canDecide })}</>
          ) : view === "required" ? (
            <>
              {/* `P-37`/B6 — what the analysis is FOR, before what to check.
                  The old copy opened on the failure mode and never said what
                  the run produces, so the reason to look carefully was there
                  and the reason to run at all was not. */}
              L&apos;analisi verifica le discrepanze, genera le categorie di lavorazione e calcola
              il margine della commessa. Si basa sui documenti qui sotto: controlla che siano
              corretti, poi continua al passo 2.
            </>
          ) : /* R5 D-1 — the ONLY KB-facing copy in this panel that did not branch
               on `canDecide`. `groupDocuments` branches, the commit bar branches,
               this did not — so an ordinary member, whose checkboxes are every
               one disabled, read "Togli la spunta a quelli che non vuoi
               sintetizzare" beside four other sentences on the same screen
               saying the decision was an administrator's. The identical defect
               was fixed one file over on `UploadView`'s KB card, with the reason
               written out; the sibling never got it. */
          kb.optional.length === 0 ? (
            /* Nothing to tick. «Togli la spunta a quelli che non vuoi
                 sintetizzare» over ZERO checkboxes is an instruction the screen
                 cannot carry out — and after `P-43` this step renders no other
                 group, so the sentence sat above an empty panel. The locked-in
                 documents (the Relazione) still go to the Knowledge Base and
                 the strip below says so; the header stops asking for a choice
                 that does not exist. */
            <>
              Questo progetto non ha documenti da scegliere per la Knowledge Base
              {kb.lockedIn.length > 0 ? ": qui sotto trovi quelli inclusi sempre." : "."}
            </>
          ) : (
            <>
              {canDecide
                ? "Scegli cosa custodire nella Knowledge Base: sempre a disposizione, oggi e per il lavoro di domani."
                : "Qui si sceglie cosa custodire nella Knowledge Base: sempre a disposizione, oggi e per il lavoro di domani. La conferma spetta a un amministratore del tuo studio."}
            </>
          )}
        </div>
        {/* D-4 — the sentence saying an invisible veto is STILL being obeyed.
            It used to live only in the Knowledge Base track card, which `P-37`
            renders on the Avanzamento tab and not on Passo 2. Passo 2 is where
            «Conferma la selezione» commits the batch, so the one screen that
            could act on the warning was the one screen without it: QA measured
            1 node on tab 3 and 0 on tab 2, over a commit that really did send
            `skip: ["d-cap"]` for a document whose checkbox was gone.

            Rendered on the KB view only. On Passo 1 the same sentence would be
            a warning about a control two tabs away. */}
        {/* `showCommit` REMOVED by `P-46` — its premise no longer holds. It was
            here because "before a start the Knowledge Base track card is still
            on screen and prints this same sentence, so without the gate it
            appeared TWICE on Passo 2". `P-46` took the track cards off the
            pre-start wizard, and the two surfaces are now provably exclusive:
            the lanes render on `activeTab === "3"`, this panel only when
            `panelMounted`, which is `activeTab !== "3"` (`UploadView.tsx:1554`).
            They cannot both be mounted.

            Leaving the gate in place would have been worse than untidy. With
            the lane gone, `showCommit` (`analysisState !== "not_started"`) was
            the ONLY thing standing between a pre-start reader and this
            sentence — so a user who vetoed a document and then corrected its
            role BEFORE pressing Avvia would carry a still-honoured veto through
            the whole wizard with nothing on screen saying so. Found by the
            independent designer, not by a test: no test covered that state. */}
        {/* R5 D-5 — `showCommit` alone, NOT `&& view === "kb"`. The track card
            covers `awaitingStart || tab 3`; this covered tab 2 only; Passo 1 of
            a started run fell between them, and one disclosure click there puts
            the vetoed row on screen wearing a green "Sempre incluso
            nell'analisi" tick with nothing to say the veto still stands — the
            exact invisibility this sentence exists to remove. */}
        {kb.vetoedNowRequired.length > 0 && (
          <div style={{ fontSize: 11.5, color: "var(--c-warn)", marginTop: 6 }}>
            {kb.vetoedNowRequired.length === 1
              ? `${kb.vetoedNowRequired[0].filename} è ora richiesto dall'analisi, ma resta ESCLUSO dalla Knowledge Base come avevi scelto.`
              : `${kb.vetoedNowRequired.length} documenti che avevi escluso sono ora richiesti dall'analisi; restano esclusi dalla Knowledge Base come avevi scelto.`}
          </div>
        )}
        {/* `onlyReview ||` — the filter must not be able to unmount its own OFF
            switch. `setOnlyReview` has one call site, this button, and it used
            to render on `needsReviewCount > 0` alone. Working through the
            flagged list is the filter's entire purpose, and the backend clears
            `needs_review` on every kind correction — so finishing the job drove
            the count to 0, took this control off the screen, and left the panel
            filtered to nothing: no rows, no «Correggi», and no way back. The
            only in-app exit left was pressing Avvia, the paid action. A reload
            is not an exit either: `setProject` has exactly one call site, inside
            `submit()`, so reloading lands the reader in Fase 1 with no project.

            One condition, no effect, no second piece of state to keep in
            agreement with this one — this guard has now been reported broken
            twice, which is where the rule says replace the mechanism rather
            than patch it. */}
        {(onlyReview || needsReviewCount > 0) && (
          <button
            className="btn btn-sm"
            style={{ marginTop: 8, height: 26, fontSize: 11.5 }}
            onClick={() => setOnlyReview(!onlyReview)}
            aria-pressed={onlyReview}
          >
            <I.Alert size={11} />{" "}
            {onlyReview ? "Mostra tutti" : `Da confermare (${needsReviewCount})`}
          </button>
        )}
      </div>

      {visibleGroups.map((g) => {
        // A group is never collapsed while it is being filtered down to the
        // flagged rows.
        //
        // It CAN be collapsed while holding a checkbox — the sentence that used
        // to stand here said otherwise, and QA falsified it: on Passo 1 the
        // Knowledge Base group is closed and its rows carry checkboxes. What
        // keeps that safe is not the disclosure, it is that the control able to
        // SPEND from those checkboxes renders only where they are visible. That
        // gate is on the commit bar below, and it is the only thing standing
        // between a collapsed group and a batch bought unseen.
        //
        // `focus` is now non-null by construction (`"all"` was deleted 2026-08-20),
        // so the focused group is never collapsible and every other one always is.
        const collapsible = g.collapsible || g.id !== focus;
        const isCollapsed = collapsible && collapsed.has(g.id) && !onlyReview;
        const docs = onlyReview ? g.docs.filter((d) => d.needsReview) : g.docs;
        if (docs.length === 0 && g.id !== "required") return null;
        const headingId = `group-${g.id}`;
        /* `P-43` — Passo 1 carries no group band at all: the prototype's mode1
           list is headerless (its rows begin straight under the intro copy,
           which already says "gira su questi quattro"), so the eyebrow, the
           note and the sunken band render only where a heading distinguishes
           one group from another — which on a one-group step it cannot. The
           group name survives for assistive tech via `aria-label`. */
        const headerless = g.id === "required" && view === "required";
        return (
          <div
            key={g.id}
            role="group"
            aria-labelledby={headerless ? undefined : headingId}
            aria-label={headerless ? g.title : undefined}
          >
            {!headerless && (
              <div
                style={{
                  padding: "9px 16px",
                  background: "var(--c-bg-sunken)",
                  borderBottom: "1px solid var(--c-line)",
                  borderTop: "1px solid var(--c-line)",
                }}
              >
                {/* `P-39` `L2-07`/`T3`/`L4-05` — one screen was printing group
                  headings in two different typefaces. A collapsible group came
                  through `btn btn-ghost btn-sm` (11.5px, sentence case, no
                  tracking, `--c-ink-2`) and a fixed one through `.t-eyebrow`
                  (11px, uppercase, `.08em`, `--c-ink-3`) — so which treatment a
                  heading received depended on which STEP you were standing on,
                  not on what the heading was. In the prototype the two carry
                  byte-identical style strings and the chevron alone says the
                  group can be opened. `.t-eyebrow` is already an exact port of
                  the prototype's eyebrow, so the button borrows it and keeps
                  only the button reset. */}
                {collapsible ? (
                  <button
                    id={headingId}
                    className="btn btn-ghost btn-sm t-eyebrow"
                    style={{
                      height: 22,
                      padding: 0,
                      color: "var(--c-ink-3)",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                    onClick={() => toggleGroup(g.id)}
                    aria-expanded={!isCollapsed}
                  >
                    {isCollapsed ? <I.ChevronD size={12} /> : <I.ChevronU size={12} />}{" "}
                    {headingFor(g, docs)}
                  </button>
                ) : (
                  <div id={headingId} className="t-eyebrow">
                    {headingFor(g, docs)}
                  </div>
                )}
                {!isCollapsed && (
                  /* `P-39` `L2-13`/`T10`/`L5-07` — this is the sentence that says
                   what a group DOES, including "nessuno dei due sistemi li
                   legge", the one place the screen states that a document will
                   be ignored entirely. It was printing in `--c-ink-4` on the
                   sunken band: contrast 2.36:1, failing even the large-text
                   floor at 11.5px, which made the most consequential line in
                   the group the palest thing on the screen. */
                  <div
                    style={{
                      fontSize: 12.5,
                      color: "var(--c-ink-instruction)",
                      marginTop: 4,
                      lineHeight: 1.45,
                      textWrap: "pretty",
                    }}
                  >
                    {g.note}
                  </div>
                )}
                {/* Scoped to the rows actually rendered here and now. Unscoped, a
                  click while the "Da confermare" filter was on reached past the
                  hidden rows and vetoed documents the user could not see. */}
                {g.id === "kb" && !isCollapsed && kb.optional.length > 0 && canDecide && (
                  <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                    {/* Both closed while a decision is in flight. Neither carried
                      `disabled` at all, so an admin sat in front of two live
                      bulk controls for the whole round trip and anything they
                      did was discarded and then erased. `kb.setAll` refuses too
                      — the attribute is the visible half, never the only one. */}
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ height: 22, fontSize: 11 }}
                      disabled={kb.isPending}
                      onClick={() => kb.setAll(true, visibleKbIds(docs))}
                    >
                      {onlyReview ? "Seleziona i visibili" : "Seleziona tutti"}
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ height: 22, fontSize: 11 }}
                      disabled={kb.isPending}
                      onClick={() => kb.setAll(false, visibleKbIds(docs))}
                    >
                      {onlyReview ? "Deseleziona i visibili" : "Deseleziona tutti"}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* `P-39` `L2-02`/`L4-02`/`L5-06` — on Passo 2 the seventeen Knowledge
                Base rows fused into one white slab. Background, opacity and
                radius already matched the prototype, but with no outline, no
                lift and no air between them a `#ffffff` row on a `#ffffff`
                panel does nothing at all: a selected document did not look
                selected, only the unselected ones looked dimmed. The prototype
                makes each row a discrete outlined card with 6px between them.
                `gap` on the container rather than a margin on the row so the
                last row does not carry a trailing space. */}
            {!isCollapsed && (
              <div
                style={{
                  padding: "8px 10px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                {docs.map((d) => (
                  <DocumentRow
                    key={d.id}
                    doc={d}
                    // A checkbox means "this is still yours to decide". Keyed on
                    // actual pending membership, not on which group the row landed
                    // in — the KB group also holds already-decided documents, and
                    // offering a checkbox for a decision already committed would
                    // invite a click that does nothing.
                    variant={
                      REQUIRED_ANALYZE_ROLES.includes(d.role)
                        ? "required"
                        : pendingIds.has(d.id)
                          ? "checkbox"
                          : "plain"
                    }
                    checked={kb.isChecked(d.id)}
                    onToggle={() => kb.toggle(d.id)}
                    canDecide={canDecide}
                    /* Separate from `canDecide` on purpose: `canDecide` also
                       chooses the group's instructions, and flipping it for the
                       duration of a request would rewrite the copy under the
                       reader mid-call. */
                    decisionBusy={kb.isPending}
                    correction={correction}
                    download={download}
                    kindOptions={kindOptions}
                  />
                ))}

                {/* A required role with no document is the most actionable fact on
                    the screen, so it gets a row in its own slot rather than a
                    warning in a sidebar the user has to connect back to a list. */}
                {/* Survives the "Da confermare" filter on purpose: a required
                    document that is entirely absent is the most actionable thing
                    on the screen, and a filter that hides it turns a warning into
                    silence at exactly the moment someone is scanning for problems. */}
                {g.id === "required" &&
                  missingRoles.map((r) => (
                    <div
                      key={r}
                      style={{
                        display: "flex",
                        gap: 10,
                        padding: "9px 12px",
                        alignItems: "flex-start",
                        borderLeft: "2px solid var(--c-warn)",
                      }}
                    >
                      <div style={{ width: 20, flexShrink: 0, paddingTop: 1 }}>
                        <I.Alert size={14} style={{ color: "var(--c-warn)" }} aria-hidden />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--c-warn)" }}>
                          {DOC_ROLE_LABEL[r]} — nessun documento riconosciuto
                        </div>
                        <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", marginTop: 2 }}>
                          Aggiungi il file, oppure correggi il tipo di un documento già caricato.
                        </div>
                        {/* `P-37`/B9 — the remedy on the row that states the
                            problem. The sentence above named both remedies and
                            offered neither: the type dropdown was on some OTHER
                            row further down (you had to already know WHICH), and
                            the drop zone was somewhere above. Both are here now.

                            The select re-types an existing document rather than
                            listing roles, which is the correct direction: the
                            missing role has no document, so the only thing to
                            pick is which of the documents you DO have is really
                            this one. It uses `changeRole`, not `changeKind` —
                            `changeKind` re-derives the role from the taxonomy
                            and would undo the very assignment being made, and
                            it also clears `needsReview`, which this does not
                            answer. */}
                        {/* D-6 — NOT gated on `readOnly`. It was, and that
                            withheld the control in exactly the state that needs
                            it: after a failed run, on the row that says
                            "correggi il tipo di un documento già caricato", in
                            a panel whose own copy promises corrections stay
                            available. Measured 1 select before a start and 0
                            after a failure. Re-typing a document is a
                            correction to the PROJECT, like the per-row
                            «Correggi» beside it, which was never gated.

                            D-9 — and it carries the busy lock its neighbour
                            carries. `useDocumentCorrection` holds one key per
                            `docId:field` and returns early while it is held, so
                            a second pick inside the round trip was dropped in
                            silence — and the controlled `value=""` erased the
                            evidence that anything had been picked. */}
                        {missingUpload.activeRole === r ? (
                          /* `P-43` — the upload in flight for THIS row. The
                             select and the drop target stand down while it
                             works (the prototype's missUpOn state): offering a
                             reassignment beside a half-sent file would let the
                             two remedies race for the same role. The percent is
                             the XHR transport's real bytes (`P-41`); at 100 the
                             server is classifying, so the line changes subject
                             instead of freezing on a number. */
                          <div style={{ marginTop: 8 }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                fontSize: 12,
                                color: "var(--c-ink-2)",
                              }}
                            >
                              <span
                                style={{
                                  flex: 1,
                                  minWidth: 0,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {missingUpload.activeName}
                              </span>
                              <span
                                style={{
                                  fontSize: 11.5,
                                  color: "var(--c-ink-3)",
                                  fontVariantNumeric: "tabular-nums",
                                }}
                              >
                                {missingUpload.pct === null
                                  ? "caricamento…"
                                  : missingUpload.pct >= 100
                                    ? "elaborazione…"
                                    : `${missingUpload.pct}%`}
                              </span>
                            </div>
                            {missingUpload.pct !== null && (
                              <div
                                role="progressbar"
                                aria-valuenow={missingUpload.pct}
                                aria-valuemin={0}
                                aria-valuemax={100}
                                aria-label="Caricamento del file"
                                style={{
                                  height: 4,
                                  background: "var(--c-bg-sunken)",
                                  borderRadius: 2,
                                  overflow: "hidden",
                                  marginTop: 6,
                                }}
                              >
                                <div
                                  style={{
                                    height: "100%",
                                    borderRadius: 2,
                                    background: "var(--c-warn)",
                                    width: `${missingUpload.pct}%`,
                                    transition: "width .15s linear",
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        ) : (
                          <>
                            {documents.length > 0 && (
                              <select
                                className="input"
                                style={{ marginTop: 8, width: "100%", maxWidth: 460, height: 34 }}
                                value=""
                                disabled={assignBusy}
                                aria-label={`Assegna un documento già caricato al ruolo ${DOC_ROLE_LABEL[r]}`}
                                onChange={(e) => {
                                  const d = documents.find((x) => x.id === e.target.value);
                                  if (!d) return;
                                  /* Same reason as the sole-holder guard below: the
                                 `disabled` attribute is correct and is not
                                 enforcement. A change event arrives at the
                                 handler regardless, and `useDocumentCorrection`
                                 would then return early on the held key and
                                 drop this silently — which is how the same
                                 write got lost three rounds running. */
                                  if (assignBusy) return;
                                  /* The refusal is enforced HERE, not only by
                                 `disabled` on the option. An attribute is a
                                 statement to the browser; a change event can
                                 still name a refused value — QA sent one and
                                 got the PATCH the label forbids. A rule that
                                 lives only in markup is a rule with no code
                                 behind it. */
                                  if (soleRequiredHolder(d) !== null) return;
                                  void correction.changeRole(d, r);
                                }}
                              >
                                <option value="">
                                  {assignBusy
                                    ? "Correzione in corso…"
                                    : documents.filter(
                                          (d) => d.role !== r && soleRequiredHolder(d) === null,
                                        ).length === 0
                                      ? /* Do not invite a choice that cannot be made.
                                       Every document here is the sole holder of
                                       another required role, so the only real
                                       remedy is to upload the file — which the
                                       line above already says. */
                                        "Nessun documento può essere riassegnato: caricane uno"
                                      : `Assegna a «${DOC_ROLE_LABEL[r]}» scegliendo un documento dalla lista`}
                                </option>
                                {documents
                                  .filter((d) => d.role !== r)
                                  .map((d) => {
                                    const sole = soleRequiredHolder(d);
                                    return (
                                      <option key={d.id} value={d.id} disabled={sole !== null}>
                                        {d.filename} — {DOC_ROLE_LABEL[d.role] ?? d.role}
                                        {sole !== null
                                          ? ` · non disponibile: è l'unico ${DOC_ROLE_LABEL[sole]}`
                                          : ""}
                                      </option>
                                    );
                                  })}
                              </select>
                            )}
                            {/* `P-43` — the prototype's second exit: upload the
                            missing file right here, into this role. The role is
                            sent as an explicit form field, and the server's own
                            rule is `resolved_role = explicit_role or
                            classification.role` — the recogniser still runs,
                            but it cannot re-route the file away from the slot
                            the user just filled. */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                margin: "10px 0 0",
                                maxWidth: 460,
                              }}
                              aria-hidden="true"
                            >
                              <span style={{ height: 1, background: "var(--c-line)", flex: 1 }} />
                              <span style={{ fontSize: 11, color: "var(--c-ink-3)" }}>oppure</span>
                              <span style={{ height: 1, background: "var(--c-line)", flex: 1 }} />
                            </div>
                            <div
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={(e) => {
                                e.preventDefault();
                                const f = e.dataTransfer.files?.[0];
                                /* First file only: the slot holds one document. The
                               busy refusal lives in `start` (UploadView), not
                               here — an attribute or a guard that exists only
                               at the pick site is one this build has already
                               shipped around twice. */
                                if (f) missingUpload.start(r, f);
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                boxSizing: "border-box",
                                width: "100%",
                                maxWidth: 460,
                                height: 44,
                                border: "1.5px dashed var(--c-line-strong)",
                                borderRadius: 8,
                                padding: "0 12px",
                                marginTop: 10,
                              }}
                            >
                              <span style={{ fontSize: 12, color: "var(--c-ink-3)", flex: 1 }}>
                                Trascina qui il file
                              </span>
                              <input
                                type="file"
                                id={`missing-file-${r}`}
                                className="sr-only"
                                aria-label={`Carica il file per ${DOC_ROLE_LABEL[r]}`}
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  // Reset so picking the same file twice re-fires.
                                  e.target.value = "";
                                  if (f) missingUpload.start(r, f);
                                }}
                              />
                              <label
                                htmlFor={`missing-file-${r}`}
                                className="btn btn-sm"
                                style={{ cursor: "pointer" }}
                              >
                                Sfoglia file
                              </label>
                            </div>
                          </>
                        )}
                        {missingUpload.errorRole === r && missingUpload.error && (
                          <div
                            role="alert"
                            style={{ marginTop: 6, fontSize: 11.5, color: "var(--c-err)" }}
                          >
                            {missingUpload.error}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                {/* `P-44` — the price-list card, the prototype's last Passo-1
                    row. Hidden under the «Da confermare» filter exactly as the
                    prototype hides it (`mode1 && !filterOn`): the filter shows
                    flagged documents, and this card holds none. */}
                {g.id === "required" &&
                  view === "required" &&
                  !onlyReview &&
                  (() => {
                    const regionalDocs = documents.filter((d) => d.role === "regional");
                    const pl = priceLists;
                    // The library rows are an answer only when the query
                    // succeeded; the uploaded rows come from the documents
                    // query and are unaffected by a price-list GET failing.
                    const editions = pl.ready ? pl.editions : [];
                    // The region of the book the SYSTEM chose, which is what
                    // the heading names.
                    //
                    // The adversarial review called this a false label above a
                    // MIXED card (`F29`, and `F25` for the pill below), and fix
                    // round 1 removed both. That removal was reversed by a
                    // product decision recorded in the wiki finding
                    // 2026-08-22-intake-p43-p41-p44 — read it there rather than
                    // trusting this comment, which cannot verify its own
                    // provenance. Both are ACCEPTED DIVERGENCES, not unnoticed
                    // defects: the cost (heading names one region while a row
                    // beneath it is another region's book) is stated on that
                    // page. The reasoning: this card is a CHECKING surface —
                    // every row states its own filename, source and provenance
                    // and carries its own Rimuovi, so the heading is a heading
                    // rather than a claim about each row.
                    const regionTitle = editions.length > 0 ? ` — ${editions[0].region}` : "";
                    const hasItems =
                      editions.length > 0 || regionalDocs.length > 0 || pl.uploading !== null;
                    return (
                      <div
                        role="group"
                        aria-label="Listini prezzi di riferimento"
                        style={{
                          display: "flex",
                          gap: 10,
                          padding: "9px 12px",
                          borderRadius: 8,
                          alignItems: "flex-start",
                          background: "var(--c-ok-bg)",
                          border: "1px solid var(--c-ok-line)",
                          borderLeft: "2px solid var(--c-ok)",
                        }}
                      >
                        <div style={{ width: 20, flexShrink: 0, paddingTop: 2 }}>
                          <I.Excel size={15} style={{ color: "var(--c-ok)" }} aria-hidden />
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              flexWrap: "wrap",
                            }}
                          >
                            <span style={{ fontSize: 13, fontWeight: 500 }}>
                              Listini prezzi di riferimento{regionTitle}
                            </span>
                            {/* Card-wide over a NON-EMPTY card, as drawn.
                                `F25` objected that it is false above an
                                uploaded row; that is an accepted divergence,
                                for the same reason as the heading — see
                                `regionTitle` above.
                                The EMPTY state is a third case the ratified
                                mock does not depict, and there the badge is
                                not a divergence but a claim about zero rows:
                                «Fonte regionale» directly above «Nessun
                                listino associato». Gated on the same
                                `hasItems` the rows are. */}
                            {hasItems && (
                              <span className="pill pill-ok">Fonte regionale · non dallo ZIP</span>
                            )}
                          </div>

                          {hasItems ? (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 6,
                                marginTop: 6,
                              }}
                            >
                              {editions.map((e) => {
                                // Bound once. Calling the helper four times
                                // inside the JSX would let a future edit
                                // change one call and not the others, and
                                // the two that matter here are the id the
                                // button downloads and the name printed
                                // beside it — they must be the same file.
                                const file = downloadableFile(e);
                                return (
                                  <div
                                    key={e.editionId}
                                    style={{
                                      display: "flex",
                                      alignItems: "baseline",
                                      gap: 8,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 11.5,
                                        minWidth: 0,
                                        flex: 1,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                      title={e.matchedOn ?? undefined}
                                    >
                                      {file ? (
                                        <button
                                          type="button"
                                          className="linklike"
                                          disabled={pl.download.isBusy(file.id)}
                                          onClick={() => {
                                            void pl.download.openId(file.id);
                                          }}
                                          style={{
                                            color: "var(--c-brand)",
                                            textDecoration: "underline",
                                            textUnderlineOffset: 2,
                                            textDecorationColor: "#c6d3e2",
                                            background: "none",
                                            border: 0,
                                            padding: 0,
                                            font: "inherit",
                                            cursor: "pointer",
                                          }}
                                        >
                                          {e.title}
                                        </button>
                                      ) : (
                                        <span style={{ color: "var(--c-ink-2)" }}>{e.title}</span>
                                      )}
                                      {file && (
                                        <span style={{ color: "var(--c-ink-3)" }}>
                                          {" "}
                                          · {file.filename}
                                        </span>
                                      )}
                                      {/* WHY there is no link, said positively
                                        (`P-58`). Omar, 29 Aug: a licensed
                                        commercial book is *"visible as a source
                                        but not downloadable"*. Without this
                                        sentence three unrelated causes render
                                        identically — the licence, an edition
                                        that owns no workbook, and a server
                                        older than `P-55` — and a customer
                                        reading a missing link cannot tell which
                                        one they are looking at. */}
                                      {!e.redistributable && (
                                        <span style={{ color: "var(--c-ink-3)" }}>
                                          {" "}
                                          · Prezzario commerciale su licenza: consultabile, non
                                          scaricabile.
                                        </span>
                                      )}
                                      {/* The hook's per-id error, RENDERED. It
                                        exists on the contract already, and a
                                        value nothing reads is not a guarantee —
                                        a failed mint would otherwise leave the
                                        title looking like an inert link and the
                                        customer with no idea the verification
                                        they were promised did not happen. */}
                                      {file && pl.download.errorFor(file.id) && (
                                        <span role="alert" style={{ color: "var(--c-err)" }}>
                                          {" "}
                                          · {pl.download.errorFor(file.id)}
                                        </span>
                                      )}
                                      <span style={{ color: "var(--c-ink-3)" }}>
                                        {" — "}
                                        {e.auto
                                          ? "Associato automaticamente in base alla regione della gara."
                                          : "Aggiunto da te."}
                                        {/* The EVIDENCE, visible rather than
                                          hover-only. A heuristic that decides
                                          which book prices a tender has to show
                                          its basis where a person reads it; a
                                          `title` attribute reaches neither a
                                          touch screen nor a printout. */}
                                        {e.auto && e.matchedOn
                                          ? ` ${truncateEvidence(e.matchedOn)}`
                                          : ""}
                                        {e.files.length > 1 ? ` ${e.files.length} file.` : ""}
                                      </span>
                                    </span>
                                    {!pl.locked && (
                                      <button
                                        className="btn btn-ghost btn-sm"
                                        style={{ color: "var(--c-err)", height: 26 }}
                                        disabled={pl.removeBusy}
                                        onClick={() => pl.remove(e.editionId)}
                                      >
                                        Rimuovi
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                              {regionalDocs.map((d) => (
                                <div
                                  key={d.id}
                                  style={{
                                    display: "flex",
                                    alignItems: "baseline",
                                    gap: 8,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 11.5,
                                      minWidth: 0,
                                      flex: 1,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      color: "var(--c-ink-2)",
                                    }}
                                    title={d.filename}
                                  >
                                    {d.filename}
                                    <span style={{ color: "var(--c-ink-3)" }}>
                                      {" "}
                                      — Caricato da te.
                                    </span>
                                  </span>
                                  {!pl.locked && (
                                    <button
                                      className="btn btn-ghost btn-sm"
                                      style={{ color: "var(--c-err)", height: 26 }}
                                      disabled={pl.removeBusy}
                                      onClick={() => pl.removeDoc(d)}
                                    >
                                      Rimuovi
                                    </button>
                                  )}
                                </div>
                              ))}
                              {pl.uploading && (
                                <div style={{ fontSize: 11.5, color: "var(--c-ink-2)" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <span
                                      style={{
                                        flex: 1,
                                        minWidth: 0,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {pl.uploading.name}
                                    </span>
                                    <span
                                      style={{
                                        color: "var(--c-ink-3)",
                                        fontVariantNumeric: "tabular-nums",
                                      }}
                                    >
                                      {pl.uploading.pct === null
                                        ? "caricamento…"
                                        : pl.uploading.pct >= 100
                                          ? "elaborazione…"
                                          : `${pl.uploading.pct}%`}
                                    </span>
                                  </div>
                                  {pl.uploading.pct !== null && (
                                    <div
                                      role="progressbar"
                                      aria-valuenow={pl.uploading.pct}
                                      aria-valuemin={0}
                                      aria-valuemax={100}
                                      aria-label="Caricamento del listino"
                                      style={{
                                        height: 4,
                                        background: "var(--c-ok-soft)",
                                        borderRadius: 2,
                                        overflow: "hidden",
                                        marginTop: 6,
                                      }}
                                    >
                                      <div
                                        style={{
                                          height: "100%",
                                          borderRadius: 2,
                                          background: "var(--c-ok)",
                                          width: `${pl.uploading.pct}%`,
                                          transition: "width .15s linear",
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              )}
                              <div
                                style={{
                                  fontSize: 11.5,
                                  color: "var(--c-ink-3)",
                                  textWrap: "pretty",
                                }}
                              >
                                Usati per il confronto prezzi. Puoi affiancarne altri, ad esempio un
                                listino aziendale o di un&apos;altra regione.
                              </div>
                            </div>
                          ) : pl.ready ? (
                            <div
                              style={{
                                fontSize: 11.5,
                                color: "var(--c-ink-3)",
                                marginTop: 4,
                                textWrap: "pretty",
                              }}
                            >
                              Nessun listino associato. Senza listino l&apos;analisi non pu&ograve;
                              essere avviata.
                            </div>
                          ) : null}

                          {/* Disclosed in EVERY not-ready state, not only the
                              empty one. As the third arm of that ternary the
                              warning disappeared the moment ANY other row was
                              on screen — an uploaded listino, or one in flight
                              — so the projects most likely to HAVE a library
                              association were exactly the ones told nothing.
                              Whether the library query failed is a fact about
                              the query, not about how full the card looks. */}
                          {!pl.ready && (
                            <div
                              style={{
                                fontSize: 11.5,
                                color: "var(--c-ink-3)",
                                marginTop: 4,
                                textWrap: "pretty",
                              }}
                            >
                              Non riusciamo a leggere i listini associati in questo momento.
                              L&apos;analisi userà comunque quelli che il progetto ha.
                            </div>
                          )}

                          {pl.error && (
                            <div
                              role="alert"
                              style={{ marginTop: 6, fontSize: 11.5, color: "var(--c-err)" }}
                            >
                              {pl.error}
                            </div>
                          )}

                          {/* No invitation to add a listino while we cannot
                              show the ones already there — the user would be
                              duplicating an association they cannot see. */}
                          {!pl.locked && pl.ready && (
                            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                              <input
                                type="file"
                                id="listino-file"
                                className="sr-only"
                                // Only what the comparison stage can read —
                                // `cme_regional_matcher.is_price_book`. (There
                                // is no glob any more; an earlier version of
                                // this comment said there was.) The server
                                // refuses the rest at every writer, and infers
                                // it at none, so this attribute is the
                                // courtesy rather than the guard.
                                accept=".xlsx"
                                aria-label="Carica un listino prezzi"
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  e.target.value = "";
                                  if (f) pl.upload(f);
                                }}
                              />
                              <label
                                htmlFor="listino-file"
                                className="btn btn-sm"
                                style={{ cursor: "pointer" }}
                              >
                                + Aggiungi listino
                              </label>
                            </div>
                          )}

                          {/* `P-47b` — the library picker.
                              The SHIP GATE for `P-47`: the auto-associator
                              declines in four cases (no unambiguous region ·
                              region legible but no edition year · region held
                              by no edition · the tender names an edition we do
                              not hold), and once a listino is mandatory each
                              one strands a project with no way forward.

                              Rendered whenever the library is an ANSWER, not
                              only when the card is empty: Omar ratified on
                              25 Aug that a project may hold more than one
                              price list, because a CME can legitimately have
                              been priced against two. */}
                          {!pl.locked && pl.ready && pl.library !== null && (
                            <div style={{ marginTop: 12 }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  color: "var(--c-ink-3)",
                                  fontSize: 11,
                                }}
                              >
                                <span style={{ flex: 1, height: 1, background: "var(--c-line)" }} />
                                oppure
                                <span style={{ flex: 1, height: 1, background: "var(--c-line)" }} />
                              </div>
                              <div
                                style={{
                                  fontSize: 11.5,
                                  color: "var(--c-ink-3)",
                                  marginTop: 8,
                                  textWrap: "pretty",
                                }}
                              >
                                {editions.length === 0 && regionalDocs.length === 0 ? (
                                  <>
                                    {/* Omar's ratified wording, 25 Aug. The mockup said
                                        «sono disponibili i prezzari di tutte le regioni»,
                                        which was false over a three-region library — the
                                        exact failure «only real values ship» exists to
                                        prevent. The regions are SERVED, so this sentence
                                        cannot go stale when an edition is seeded.

                                        «Non abbiamo trovato» is a claim about a SEARCH, and
                                        an empty card is not evidence one happened. When the
                                        user removed the edition themselves, saying we failed
                                        to find it is false and it hides the one thing they
                                        need to know — that the book they vetoed is still in
                                        the list. `previouslyRemoved` is served for exactly
                                        this and had no consumer; a field is not a property
                                        of the system until something reads it. */}
                                    {pl.library.editions.some((e) => e.previouslyRemoved)
                                      ? "Hai rimosso il listino associato a questa gara. Puoi rimetterlo o sceglierne un altro dalla libreria"
                                      : "Non abbiamo trovato il listino esatto per questa gara. Puoi sceglierne uno dalla libreria"}
                                    {pl.library.regions.length > 0 ? (
                                      <>: al momento contiene {joinRegions(pl.library.regions)}.</>
                                    ) : (
                                      "."
                                    )}
                                  </>
                                ) : (
                                  <>Puoi aggiungerne un altro dalla libreria.</>
                                )}
                              </div>
                              <select
                                aria-label="Seleziona un listino dalla libreria"
                                disabled={pl.addBusy}
                                value=""
                                onChange={(e) => {
                                  const id = e.target.value;
                                  // The select is a COMMAND, not a value: it
                                  // resets to the placeholder and the chosen
                                  // edition appears as a row above. Binding it
                                  // to state would leave a selection on screen
                                  // that no longer describes anything.
                                  e.target.value = "";
                                  if (id) pl.add(id);
                                }}
                                style={{
                                  marginTop: 8,
                                  width: "100%",
                                  padding: "8px 10px",
                                  fontSize: 12.5,
                                  borderRadius: 6,
                                  border: "1px solid var(--c-line)",
                                  background: "var(--c-bg)",
                                  color: "var(--c-ink-1)",
                                }}
                              >
                                <option value="">Seleziona un listino dalla libreria</option>
                                {pl.library.editions.map((e) => (
                                  <option
                                    key={e.id}
                                    value={e.id}
                                    // An edition owning no workbook stages
                                    // nothing, so choosing it would leave the
                                    // project exactly as blocked. Shown and
                                    // disabled WITH the reason, rather than
                                    // omitted — an option that vanishes is a
                                    // question the user cannot ask.
                                    disabled={e.fileCount === 0 || e.associated}
                                  >
                                    {e.region} {e.year} — {e.title}
                                    {e.associated
                                      ? " (già associato)"
                                      : e.fileCount === 0
                                        ? " (nessun file disponibile)"
                                        : ""}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
              </div>
            )}
          </div>
        );
      })}

      {correction.error && (
        <div style={{ padding: "10px 16px", fontSize: 12, color: "var(--c-err)" }} role="alert">
          Correzione non salvata: {correction.error}
        </div>
      )}

      {/* The KB commit point, at the end of the list it concerns.
          Absent before the run starts: there the single "Avvia analisi e
          Knowledge Base" button is the commit, and a second control able to send
          the same batch would be two buttons that both spend money on the same
          screen — with the analysis attached to only one of them.

          R4-02 — and absent on Passo 1, where the group it commits is CLOSED.
          This was `{showCommit && …}` alone, which was safe only for as long as
          a started run rendered the undivided "all" panel and expanded both
          groups. Deleting `"all"` gave every started run a focus, and the bar
          survived onto a step where its own rows are not on screen: QA pressed
          it from Passo 1 and sent `approve: ["d-info","d-cap","d-psc"]` with
          zero checkboxes rendered. The rule it broke is stated twice, verbatim,
          twenty lines above — a group carrying a commit is open on the step
          whose button commits it. */}
      {showCommit && view === "kb" && (
        <div
          style={{
            padding: "12px 16px",
            borderTop: "1px solid var(--c-line)",
            background: "var(--c-bg-elev)",
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          {canDecide ? (
            <>
              <button
                className="btn btn-primary"
                /* The guard is HERE as well as in `disabled`. A change or click
                   event reaches a handler whatever the attribute says, and a
                   rule that exists only as an attribute leaves no branch a test
                   can reach — this build has already shipped that mistake twice
                   in this file. */
                /* The guard is HERE as well as in `disabled`: a click event
                   reaches a handler whatever the attribute says. */
                onClick={() => {
                  if (commitBlockedByFilter) return;
                  void kb.submit();
                }}
                disabled={
                  kb.isPending || commitBlockedByFilter || kb.approve.length + kb.skip.length === 0
                }
              >
                {kb.isPending ? "Invio…" : `Conferma la selezione (${kb.approve.length})`}
              </button>
              <div style={{ fontSize: 11.5, color: "var(--c-ink-3)" }}>
                {kb.summary}
                <div>
                  {analysisState === "running"
                    ? "L'analisi è già in corso e non aspetta questa scelta."
                    : "L'analisi è già finita: questa scelta riguarda solo la Knowledge Base."}
                </div>
              </div>
            </>
          ) : (
            <div style={{ fontSize: 12, color: "var(--c-ink-3)" }}>
              Solo un amministratore può confermare questa scelta. Puoi comunque correggere i tipi
              di documento qui sopra.
            </div>
          )}
          {/* A refusal names its reason and its exit. «Mostra tutti» is the
              existing toggle at the top of this card — named, not duplicated:
              two controls doing one thing is how a reader starts wondering
              whether they differ. Worded for BOTH corpora: on AOSTA the flagged
              documents are a SUBSET of the KB batch, so "sta nascondendo i
              documenti di questa selezione" was false there — it hid one of
              three. "non mostra tutti" is true on both. */}
          {commitBlockedByFilter && canDecide && (
            <div style={{ fontSize: 11.5, color: "var(--c-warn)", width: "100%" }} role="status">
              Con il filtro «Da confermare» attivo l&apos;elenco non mostra tutti i documenti di
              questa selezione. Rimetti l&apos;elenco su «Mostra tutti» qui sopra: non si conferma
              quello che non è sullo schermo.
            </div>
          )}
          {kb.done && (
            <div style={{ fontSize: 12, color: receiptColor(kb.receiptTone), width: "100%" }}>
              <KbReceipt decision={kb} />
            </div>
          )}
          {kb.error && (
            <div style={{ fontSize: 12, color: "var(--c-err)", width: "100%" }} role="alert">
              {kb.error}
            </div>
          )}
          {/* Names the FILE. Deleting the undivided "all" view gave every
              started run a focus, which collapsed the group these rows live in —
              so on this step the batch includes documents whose row is behind a
              disclosure. They carry no checkbox by product decision, so nothing
              is out of reach; what was missing is being able to tell WHICH
              document the sentence is about without opening anything. */}
          {kb.lockedIn.length > 0 && (
            <div style={{ fontSize: 11, color: "var(--c-ink-3)", width: "100%" }}>
              {kb.lockedIn.length === 1
                ? `Include anche ${kb.lockedIn[0].filename}, richiesto dall'analisi: sempre incluso e senza casella.`
                : `Include anche ${kb.lockedIn.length} documenti richiesti dall'analisi (${kb.lockedIn.map((d) => d.filename).join(", ")}): sempre inclusi e senza casella.`}
            </div>
          )}
        </div>
      )}
      {/* The same reconciling line, for the step that has no commit bar.
          `kbProposedNote` counts every `pending` document — the locked-in
          Relazione included — while this group's header counts only the ones
          carrying a checkbox. Both are true and they differ by exactly the
          locked-in rows, so in the WIZARD, where the bar below is not rendered,
          the screen printed "3 documenti proposti per la Knowledge Base" above
          "Proposti per la Knowledge Base (2)" with nothing to reconcile them.
          Measured by the co-presence walk at 38 findings across 28 cells once
          its keyword check was case-folded — the header capitalises the P, and
          `line.includes` had been case-sensitive, so nothing had ever seen it.
          `regression: no`; live on QA today. */}
      {!showCommit && view === "kb" && kb.lockedIn.length > 0 && (
        <div
          style={{
            padding: "8px 16px",
            borderTop: "1px solid var(--c-line)",
            fontSize: 11,
            color: "var(--c-ink-3)",
          }}
        >
          {kb.lockedIn.length === 1
            ? `Include anche ${kb.lockedIn[0].filename}, richiesto dall'analisi: sempre incluso e senza casella.`
            : `Include anche ${kb.lockedIn.length} documenti richiesti dall'analisi (${kb.lockedIn.map((d) => d.filename).join(", ")}): sempre inclusi e senza casella.`}
        </div>
      )}
      {/* The outcome of a KB submission still has to be visible when the bar
          that would normally carry it is not rendered — `startRun` submits the
          batch from the wizard, and a failure there must not be silent. */}
      {/* The reason a non-admin's checkboxes are disabled, rendered wherever they
          are disabled. It used to sit inside `{showCommit && …}` — which was
          always true before `P-31`, because the panel only ever rendered with a
          run in flight. `P-31` made `showCommit` false for the entire wizard, so
          suppressing the commit bar silently took the refusal's REASON with it,
          leaving a member with dead checkboxes, no explanation, and a heading
          telling them to untick things. Deleting a mechanism takes its side
          effects. */}
      {!showCommit && !canDecide && kb.optional.length > 0 && (
        <div
          style={{
            padding: "10px 16px",
            borderTop: "1px solid var(--c-line)",
            fontSize: 12,
            color: "var(--c-ink-3)",
          }}
        >
          Solo un amministratore può confermare questa scelta. Puoi comunque correggere i tipi di
          documento qui sopra.
        </div>
      )}
      {!showCommit && (kb.done || kb.error) && (
        <div
          style={{
            padding: "10px 16px",
            borderTop: "1px solid var(--c-line)",
            fontSize: 12,
            color: kb.error ? "var(--c-err)" : receiptColor(kb.receiptTone),
          }}
          role={kb.error ? "alert" : undefined}
        >
          {kb.error ?? <KbReceipt decision={kb} />}
        </div>
      )}
    </div>
  );
}
